import { inject, Injectable, signal } from '@angular/core';
import { ConfirmationModalData } from '../components/confirmation-modal';

/**
 * Service used by the unsaved-changes guard to show a single, app-wide
 * "Leave with unsaved changes?" modal. The root app component hosts the modal
 * and binds it to this service's state.
 */
@Injectable({ providedIn: 'root' })
export class LeaveWithUnsavedChangesService {
  readonly isVisible = signal(false);
  readonly modalData = signal<ConfirmationModalData | null>(null);

  private resolvePending: ((value: boolean) => void) | null = null;

  /**
   * Shows the leave-confirmation modal and returns a Promise that resolves to
   * true (user chose Leave) or false (user chose Stay). Optional custom message
   * overrides the default.
   */
  confirmLeave(customMessage?: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.resolvePending = resolve;
      this.modalData.set({
        title: 'Unsaved changes',
        message:
          customMessage ??
          'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
        itemName: '',
        confirmButtonText: 'Leave',
        confirmButtonClass: 'btn-warning',
        iconClass: 'bi bi-exclamation-triangle text-warning',
        details: 'You can stay to save your work.',
      });
      this.isVisible.set(true);
    });
  }

  onConfirm(): void {
    this.resolvePending?.(true);
    this.resolvePending = null;
    this.isVisible.set(false);
    this.modalData.set(null);
  }

  onCancel(): void {
    this.resolvePending?.(false);
    this.resolvePending = null;
    this.isVisible.set(false);
    this.modalData.set(null);
  }
}
